import { useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";

import type { CrmColumnItem } from "app/pages/sales-intelligence/sub-modules/management/types";

import { MAX_FILTERS_QUANTITY } from "../constants";
import { useCrmEnrichmentContext } from "../contexts/CrmEnrichmentContext";
import crmEnrichmentFiltersService from "../services/filters";
import crmEnrichmentTrackingService from "../services/tracking/trackingService";
import {
    addFilter,
    deleteFilter,
    initializeFiltersFromJob,
    setFilterField,
    setFilterValue,
    setIsApplyFiltersSelected,
} from "../store/action-creators";
import { selectFilters } from "../store/selectors";
import type { CrmEnrichmentJobFilterDTO, CrmEnrichmentJobFilterType, Operator } from "../types/types";

type HookValue = {
    initializeFromJob: (
        initialFilters: readonly CrmEnrichmentJobFilterDTO[],
        initialColumns: readonly CrmColumnItem[],
    ) => void;
    onApplyFiltersChange: (value: boolean) => void;
    onAddFilter: () => void;
    onDeleteFilter: (id: number) => void;
    onFilterChange: <K extends keyof CrmEnrichmentJobFilterType>(params: {
        id: number;
        key: K;
        value: CrmEnrichmentJobFilterType[K];
    }) => void;
    onCrmFieldFilterChange: (params: { id: number; field: string }) => void;
};

const useFiltersActions = (columns: readonly CrmColumnItem[]): HookValue => {
    const dispatch = useDispatch();

    const { crmProvider } = useCrmEnrichmentContext();

    const filters = useSelector(selectFilters);

    const onApplyFiltersChange = useCallback(
        (value: boolean) => {
            if (value && filters.length === 1) {
                crmEnrichmentTrackingService.filters.trackAdd(1, crmProvider);
            } else {
                crmEnrichmentTrackingService.filters.trackSelectAllEntities(crmProvider);
            }

            dispatch(setIsApplyFiltersSelected(value));
        },
        [filters.length, crmProvider],
    );

    const onAddFilter = useCallback(() => {
        if (filters.length < MAX_FILTERS_QUANTITY) {
            crmEnrichmentTrackingService.filters.trackAdd(filters.length + 1, crmProvider);

            dispatch(addFilter());
        }
    }, [filters.length, crmProvider]);

    const onDeleteFilter = useCallback(
        (id: number) => {
            crmEnrichmentTrackingService.filters.trackRemove(filters.length - 1, crmProvider);

            dispatch(deleteFilter(id));
        },
        [filters.length, crmProvider],
    );

    const onFilterChange = useCallback(
        <K extends keyof CrmEnrichmentJobFilterType>({
            id,
            key,
            value,
        }: {
            id: number;
            key: K;
            value: CrmEnrichmentJobFilterType[K];
        }) => {
            if (key === "operator") {
                crmEnrichmentTrackingService.filters.trackSelectOperator(value as Operator, crmProvider);
            }

            dispatch(setFilterValue({ id, key, value }));
        },
        [crmProvider],
    );

    const onCrmFieldFilterChange = useCallback(
        ({ id, field }: { id: number; field: string }) => {
            const column = columns.find((c) => c.label === field);
            const defaultFilter = crmEnrichmentFiltersService.getDefaultFilter();

            dispatch(
                setFilterField({
                    id,
                    field,
                    dataType: column?.dataType ?? defaultFilter.dataType,
                    pickListItems: Object.values(column?.pickListItems ?? []),
                }),
            );
        },
        [columns],
    );

    const initializeFromJob = useCallback(
        (initialFilters: readonly CrmEnrichmentJobFilterDTO[], initialColumns: readonly CrmColumnItem[]) => {
            if (initialFilters.length) {
                const hydratedFilters = initialFilters.map((filter, index) => ({
                    ...crmEnrichmentFiltersService.createFilterFromDTO(filter, initialColumns),
                    id: index + 1,
                }));

                dispatch(
                    initializeFiltersFromJob({
                        filters: hydratedFilters,
                        isApplyFiltersSelected: true,
                    }),
                );
            } else {
                dispatch(setIsApplyFiltersSelected(false));
            }
        },
        [],
    );

    return {
        initializeFromJob,

        onApplyFiltersChange,
        onAddFilter,
        onDeleteFilter,
        onFilterChange,
        onCrmFieldFilterChange,
    };
};

export default useFiltersActions;
